import { SPECIALTY } from 'common/constants';

import { StepKey } from '../StepKey';

export const PATIENT_FUNNEL_CONFIG = 'patientFunnelConfig';
export const PATIENT_FUNNEL_STEPS = 'patientFunnelSteps';
export const PATIENT_FUNNEL = 'patientFunnel';
export const PATIENT_FUNNEL_MARKETING = 'patientFunnelMarketing';
export const PATIENT_FUNNEL_PREFILL_DATA = 'patientFunnelPrefillData';

export const METADATA_PROPERTIES = {
  COUNT: 'count',
  SKIPPED: 'skipped',
};

export const mapToMatch = (offer, referralPromotion, user, marketing, data) => {
  const getStepData = (stepName) => data[stepName]?.data;

  return {
    specialty: SPECIALTY.dentist,
    location: getStepData(StepKey.LOCATION),
    other: {
      alternate: false,
      attitude: getStepData(StepKey.ATTITUDE),
      availability: 'specify',
      specificAvailabilities: getStepData(StepKey.AVAILABILITY),
      criteria: getStepData(StepKey.CRITERIA),
      dentalAnxiety: getStepData(StepKey.ANXIETY),
      distanceWeight: 75,
      doctorGender: generateGender(data),
      goals: getStepData(StepKey.GOALS),
      howSoon: getStepData(StepKey.HOW_SOON),
      hasInsurance: getStepData(StepKey.INSURANCE)?.hasInsurance,
      insurance: getStepData(StepKey.INSURANCE) || {},
      insurancePlan: getStepData(StepKey.INSURANCE)?.plan,
      isClinicUser: (user?.editable?.clinics || []).length > 0,
      isPatient: (user?.editable?.patients || []).length > 0,
      lastVisited: getStepData(StepKey.LAST_VISITED),
      location: getStepData(StepKey.LOCATION),
      marketingConsent: getStepData(StepKey.CONTACT),
      reason: getStepData(StepKey.REASON_DETAILS)?.reason,
      reasonRaw: generateRawReason(data),
      subReasonIds: mapReasonsToApi(data)?.subReasonIds || null,
      subReasons: mapReasonsToApi(data)?.subReasons || [],
      givenReason: getStepData(StepKey.REASON_DETAILS)?.urgentReason
        ?.additional,
      otherReason: getStepData(StepKey.REASON_DETAILS)?.other,
      triageResponse: getStepData(StepKey.REASON)?.triageResponse,
      userId: user?.id,
      affiliates: marketing?.affiliates || {},
      dmProps: generateDmProps(offer, referralPromotion, marketing),
      unbounceProps: marketing?.unbounceProps || {},
      utmTags: marketing?.utmTags || {
        source: '',
        medium: '',
        campaign: '',
        content: '',
        term: '',
      },
    },
  };
};

export const getFunnelConfiguration = (funnelStorage, allConfigs) => {
  if (!funnelStorage.config) {
    return null;
  }

  const rawConfig = allConfigs.find((c) => c.id === funnelStorage.config.id);
  if (!rawConfig) {
    return null;
  }

  const branchName = funnelStorage.config.branchName;
  const order = rawConfig.branches?.[branchName] || rawConfig.order;

  const config = {
    id: rawConfig.id,
    testId: funnelStorage.config.test,
    variant: funnelStorage.config.variant,
    branchName,
    trafficSplit: rawConfig.trafficSplit,
    steps: order.map((step) => step.name),
  };

  if (rawConfig.segment) {
    config.segment = {
      countries: [rawConfig.segment.country.name],
      localities: rawConfig.segment.locality,
    };
  }

  if (rawConfig.markets?.length) {
    config.markets = rawConfig.markets;
  }

  return config;
};

const generateGender = (data) => {
  const answer = data[StepKey.GENDER]?.data;

  // API expects null in this case
  if (answer === 'not-important') {
    return null;
  }

  return answer;
};

const generateRawReason = (data) => {
  const answer = data[StepKey.REASON_DETAILS]?.data || {};

  return {
    overall: {
      name: answer.reason,
      id: answer.reasonId,
    },
  };
};

const generateDmProps = (offer, referralPromotion, marketing) => {
  const dmProps = {};

  if (offer?.slug) {
    dmProps.promo_code = offer.slug;
  }

  if (referralPromotion) {
    dmProps.referral_promo_id = referralPromotion.id;
  }

  if (marketing?.dmProps) {
    dmProps.referral_code = marketing.dmProps.referral_code;
    dmProps.referral_promo = marketing.dmProps.referral_promo;
  }

  return dmProps;
};

const mapReasonsToApi = (data) => {
  const reasonDetails = data[StepKey.REASON_DETAILS]?.data || {};
  const { treatments, urgentReason } = reasonDetails;

  if (treatments) {
    return {
      subReasonIds: treatments
        .map((treatment) => treatment.id)
        .filter((treatment) => treatment !== 'other'),
      subReasons: treatments.map((treatment) => treatment.title),
    };
  }

  if (urgentReason) {
    return {
      subReasonIds: null, // API doesn't require IDs for urgent reasons
      subReasons: [urgentReason.title], // API requires this field to be an array
    };
  }

  return null;
};
